import { Component } from 'react';

/**
 * Per-route error boundary. Catches render-phase errors inside one
 * route so a crash in (say) MapPage doesn't take down the whole app
 * shell — the user keeps their nav and can switch to another page.
 *
 * The top-level Sentry.ErrorBoundary in main.jsx still wraps
 * everything as a last-resort net; this one runs first so the user
 * gets a scoped recovery option (Reload page) instead of the whole
 * shell going to the generic fallback.
 */
export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recovering: false, staleBuild: false };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    if (window.__recoverStaleChunk?.(error)) {
      this.setState({ recovering: true });
      return;
    }
    // Auto-recovery declined — almost always its cooldown, which fires
    // when a reload has just happened. The error is still a stale
    // build, and saying "this page hit an error" sends somebody
    // debugging a page that is fine.
    if (window.__looksLikeStaleChunk?.(error)) {
      this.setState({ staleBuild: true });
    }
    // Mirror to console so devs see it locally; Sentry will pick it
    // up via its window.error listener even though we caught it.
    console.error('[RouteErrorBoundary]', this.props.routeName || 'unknown route', error, info);
  }
  reset = () => this.setState({ error: null, recovering: false, staleBuild: false });

  // Unregister the worker and drop the caches before navigating. A
  // plain reload asks the same stale service worker for the same stale
  // index and gets the same 404 — the button has to actually clear it.
  hardReload = () => {
    this.reset();
    if (typeof window.__forceFreshAppShell === 'function') {
      window.__forceFreshAppShell();
      return;
    }
    window.location.reload();
  };
  render() {
    if (this.state.recovering) {
      return (
        <div style={{
          maxWidth: 480,
          margin: '4rem auto',
          padding: '2rem',
          textAlign: 'center',
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          borderRadius: 0,
        }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#0f172a' }}>
            Updating App
          </h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
            A new version was deployed. Refreshing the app shell now.
          </p>
        </div>
      );
    }
    if (!this.state.error) return this.props.children;
    if (this.state.staleBuild) {
      return (
        <div style={{
          maxWidth: 480, margin: '4rem auto', padding: '2rem', textAlign: 'center',
          border: '1px solid #e2e8f0', background: '#ffffff', borderRadius: 0,
        }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#0f172a' }}>
            A new version was deployed
          </h2>
          <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#475569' }}>
            This tab is still running the previous build, so part of the app could not load.
            Nothing is wrong with your work — clearing the cached version fixes it.
          </p>
          <button
            type="button"
            data-stale-build-reload
            onClick={this.hardReload}
            style={{
              padding: '0.5rem 1.25rem', background: '#003279', color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
            }}
          >
            Load the new version
          </button>
        </div>
      );
    }
    return (
      <div style={{
        maxWidth: 480,
        margin: '4rem auto',
        padding: '2rem',
        textAlign: 'center',
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        borderRadius: 0,
      }}>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#0f172a' }}>
          This page hit an error
        </h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#475569' }}>
          {this.props.routeName ? `${this.props.routeName} couldn't render. ` : ''}
          The rest of the app is still working — you can switch pages
          from the menu, or try reloading this one.
        </p>
        <button
          onClick={this.hardReload}
          style={{
            padding: '0.5rem 1.25rem',
            background: '#003279', color: '#fff',
            border: 'none', cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 500,
          }}
        >
          Reload this page
        </button>
      </div>
    );
  }
}
